"use client";

import { Doc } from "@convex/_generated/dataModel";
import { Card, CardContent } from "@/components/ui/card";
import { formatMiles, formatDuration } from "@/lib/geo-utils";
import { Car, Clock, Link as LinkIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface ShiftCardProps {
  shift: Doc<"shifts">;
  onClick?: () => void;
  showLinkPrompt?: boolean;
}

export function ShiftCard({ shift, onClick, showLinkPrompt }: ShiftCardProps) {
  const duration = shift.endedAt
    ? shift.endedAt - shift.startedAt - (shift.pauseDuration || 0)
    : Date.now() - shift.startedAt - (shift.pauseDuration || 0);

  const isActive = shift.status !== "completed";

  return (
    <Card
      className={cn(
        "cursor-pointer transition-colors hover:bg-accent",
        isActive && "border-green-500/50"
      )}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <Car className="h-4 w-4 text-muted-foreground" />
              <span className="font-semibold">
                {formatMiles(shift.totalMiles)}
              </span>
              {isActive && (
                <span className="text-xs bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 px-2 py-0.5 rounded-full">
                  Active
                </span>
              )}
            </div>
            <div className="flex items-center gap-4 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {formatDuration(duration)}
              </span>
              <span>
                {new Date(shift.startedAt).toLocaleDateString()}
              </span>
            </div>
          </div>

          <div className="text-right">
            <div className="text-sm text-muted-foreground">
              {new Date(shift.startedAt).toLocaleTimeString([], {
                hour: "2-digit",
                minute: "2-digit",
              })}
              {shift.endedAt && (
                <>
                  {" - "}
                  {new Date(shift.endedAt).toLocaleTimeString([], {
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </>
              )}
            </div>
            {showLinkPrompt && (
              <div className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 mt-1 justify-end">
                <LinkIcon className="h-3 w-3" />
                Tap to link
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
